import { Paper, SxProps, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TableSortLabel, Theme } from "@mui/material"
import { TablePaginator } from "../TablePaginator/TablePaginator"
import { useState } from "react"

interface ConsClubTableHeader {
    key: string
    label: string
    sortable: boolean
}

type RowType = RowField[]

interface RowField {
    key: string
    value: any // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface ConsClubTableProps {
    sx?: SxProps<Theme>
    headers: ConsClubTableHeader[]
    tableLabel: string
    totalCount: number
    rows: RowType[]
    columnRenderers?: Record<string, (value: any) => React.ReactNode> // eslint-disable-line @typescript-eslint/no-explicit-any
    onSizeChange: (size: number) => void
    onPageChange: (page: number) => void
    onSortChange: (sort: string) => void
    onSortDirectionChange: (sortDirection: 'asc' | 'desc') => void
}

export const ConsClubTable = ({
    sx,
    headers,
    tableLabel,
    totalCount,
    rows,
    columnRenderers,
    onSizeChange,
    onPageChange,
    onSortChange,
    onSortDirectionChange,
}: ConsClubTableProps) => {

    const [currentPage, setCurrentPage] = useState<number>(0)
    const [currentSize, setCurrentSize] = useState<number>(25)
    const [sort, setSort] = useState<string>('')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

    const handleSortChange = (key: string) => () => {
        if (sort === key) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
            onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSort(key)
            onSortChange(key)
            setSortDirection('asc')
            onSortDirectionChange('asc')
        }
    }

    const handleSetCurrentPage = (page: number) => {
        setCurrentPage(page)
        onPageChange(page)
    }

    const handleSetCurrentSize = (size: number) => {
        setCurrentSize(size)
        onSizeChange(size)
    }

    const renderRow = (row: RowType, index: number) => {
        
        return (
            <TableRow key={index}>
                {
                    row.map((x, i) => 
                    {
                        if (columnRenderers && columnRenderers[x.key]) {
                            return <TableCell key={i}>{columnRenderers[x.key](x.value)}</TableCell>
                        }
                        return <TableCell key={i}>{
                            x.value instanceof Date ? x.value.toLocaleDateString() : x.value
                        }</TableCell>
                    }
                    )
                }
            </TableRow>
        )
    }

    return (
        <TableContainer sx={sx} component={Paper}>
            <Table sx={{ minWidth: 500 }} aria-label={tableLabel}>
                <TableHead>
                    <TableRow>
                        {
                            headers.map(x => 
                                <TableCell key={x.key} sortDirection={sort === x.key ? sortDirection : false}>
                                    {
                                        x.sortable ?
                                            <TableSortLabel
                                                active={sort === x.key}
                                                direction={sort === x.key ? sortDirection : 'asc'}
                                                onClick={handleSortChange(x.key)}
                                            >
                                                {x.label}
                                            </TableSortLabel> :
                                            x.label
                                    }
                                </TableCell>
                            )
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    { rows.map(renderRow) }
                </TableBody>
                <TableFooter>
                    <TablePaginator
                        colSpan={headers.length}
                        currentPage={currentPage}
                        setCurrentPage={handleSetCurrentPage}
                        currentSize={currentSize}
                        setCurrentSize={handleSetCurrentSize}
                        totalCount={totalCount}
                    />
                </TableFooter>
            </Table>
        </TableContainer>
    )
}